
  import { Injectable, NgZone } from '@angular/core';
  import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
  import { Parser } from 'expr-eval';
  import { BehaviorSubject, Subject } from 'rxjs';
  import { DynamicFieldConfig, DynamicFormLayout, EntityTaxConfig } from '../models/dynamic-form.models';
  import { GraphCompiledEngine } from './graph-compiled.engine';
  import { TransactionEngine } from '../transaction-engine/transaction-engine';
  import { SequenceService } from './system/sequence.service';
import { DateUtils } from '../utils/date-utils';
import { DateValidators } from '../utils/DateValidators';

  @Injectable({ providedIn: 'root' })
  export class DynamicFieldService {
    private parser = new Parser();
    private eventBus: Record<string, Subject<any>> = {};
    private visibilityMap: Record<string, boolean> = {};
    private visibility$ = new BehaviorSubject<Record<string, boolean>>({});
    private suppressDataFlow = false;
    private expressionCache = new Map<string, any>();
    private uidCounter = 0;
    private stateDependencyMap = new Map<string,string[]>();
    private visibilityDependencyMap = new Map<string,string[]>();
    private requiredDependencyMap = new Map<string,string[]>();
    private repeaterTriggerSubscriptions =new Set<string>();
    private expressionSubscriptions =new Set<string>();

    constructor(private fb: FormBuilder,
                private graph: GraphCompiledEngine,
                private tx: TransactionEngine,
                private sequenceService: SequenceService,
                private zone: NgZone) {
      this.parser.functions.sum = (arr: any[]) => {
        if (!Array.isArray(arr)) return 0;
        return arr.reduce((t, v) => t + Number(v || 0), 0);
      };
    }

    private compileExpressions(layout: DynamicFormLayout) {

      this.graph.clear();

      Object.values(layout.fieldsMap)
        .filter(f => !!f.computedExpression)
        .forEach(f => {

          this.graph.compile(
            f.name,
            f.computedExpression!
          );

        });
    }

    private setupAutoNumber(
      form: FormGroup,
      layout: DynamicFormLayout
    ) {

      const fields =
        Object.values(layout.fieldsMap)
          .filter(f => f.autoNumber?.enabled);

      if (!fields.length) return;

      fields.forEach(field => {

        const cfg = field.autoNumber!;

        const ctrl =
          form.get(field.name);

        if (!ctrl) return;

        // readonly
        // if (cfg.readonly) {
        //   ctrl.disable({
        //     emitEvent: false
        //   });
        // }
         if (cfg.allowManualEdit !== true) {
          ctrl.disable({
            emitEvent: false
          });
        }

        // initial generation
        if (
          cfg.generateOn === 'create' &&
          !ctrl.value
        )
        {
          this.generateAutoNumber(
              form,
              field.name,
              cfg
          );
        }

        // refresh fields
        (cfg.refreshOn || [])
          .forEach(dep => {

            form.get(dep)
              ?.valueChanges
              .subscribe(() => {

                  const current =
                    ctrl.getRawValue();

                  if (
                    current &&
                    current.toString().trim() !== ''
                  ) {
                    return;
                  }

                  this.generateAutoNumber(
                    form,
                    field.name,
                    cfg
                  );

              });

          });

      });
    }

    public refreshAutoNumbers(
      form: FormGroup,
      layout: DynamicFormLayout
    ): void {

      const fields =
        Object.values(layout.fieldsMap)
          .filter(f => f.autoNumber?.enabled);

      fields.forEach(field => {

        const cfg = field.autoNumber!;

        this.generateAutoNumber(
          form,
          field.name,
          cfg
        );

      });

    }

    private async generateAutoNumber(
      form: FormGroup,
      fieldName: string,
      cfg: any
    ) {

      const ctrl =
        form.get(fieldName);

      if (!ctrl) return;

      const org =
        this.normalizeValue(
          form.get('org_id')?.value
        );

      const branch =
        this.normalizeValue(
          form.get('branch_id')?.value
        );

      try {

        const value =
          await this.sequenceService.preview(
            cfg.entity,
            org,
            branch
          );

        ctrl.setValue(
          value,
          {
            emitEvent: false
          }
        );

      } catch (err) {

        console.error(
          'AUTO NUMBER ERROR',
          err
        );
      }
    }

    buildForm(layout: DynamicFormLayout): FormGroup {

      this.dependencySubscriptions.clear();
      this.expressionSubscriptions.clear();
      this.repeaterTriggerSubscriptions.clear();
      //
      const group: Record<string, any> = {};

      const byParent: Record<string, DynamicFieldConfig[]> = {};

      Object.values(layout.fieldsMap).forEach(f => {
        if (f.parentField) {
          byParent[f.parentField] ??= [];
          byParent[f.parentField].push(f);
        }
      });

      // HEADER CONTROLS
      Object.values(layout.fieldsMap)
        .filter(f => !f.parentField)
        .forEach(f => {
          group[f.name] =
            new FormControl(
              null,
              this.buildValidators(f)
            );
        });

      // LINE ARRAYS
      Object.keys(byParent).forEach(parent => {
        group[parent] =
          this.fb.array<FormGroup>([]);
      });

      const form = this.fb.group(group);
      this.initializeFieldDefaults(form,layout);
      this.setupAutoNumber(form, layout);
      this.compileExpressions(layout);   // ⭐ REQUIRED
      this.setupLineComputed(form, layout); // keep line computes
      this.setupRepeaterTriggers(form,layout);
      this.initializeRepeaterDefaults(form, layout);
     
      // initialize once
      this.applyVisibility(
        form,
        layout
      );

      this.applyStateRules(
        form,
        layout
      );

      this.applyRequired(
        form,
        layout
      );

      this.applySoftMax(
        form,
        layout
      );

      // transaction engine
      this.tx.attach(form, {

        dataFlow: (changed) =>
          this.executeDataFlow(
            form,
            layout,
            changed
          ),

        compute: (changed) =>
          this.executeCompiled(
            form,
            changed
          )

      });

      //
      

      // this.initializeRepeaterDefaults(
      //   form,
      //   layout
      // );

      return form;
    }

    private initializeFieldDefaults(
      form: FormGroup,
      layout: DynamicFormLayout
    ): void {

      Object.values(layout.fieldsMap)
        .forEach(field => {

          const control =
            form.get(field.name);

          if (
            !control ||
            control.value
          ) {
            return;
          }

          // =====================
          // Date Defaults
          // =====================

          if (
            field.type === 'datepicker' &&
            field.dateConfig
          ) {

            switch (
              field.dateConfig.defaultValue
            ) {

              case 'today':

                control.patchValue(
                  DateUtils.today(),
                  {
                    emitEvent:false
                  }
                );

                break;
            }
          }

        });

    }

    private executeCompiled(form: FormGroup, changed: string) {

    const visited = new Set<string>();

    const run = (fieldName: string) => {

      if (visited.has(fieldName)) return;
      visited.add(fieldName);

      const targets =
        this.graph.getDependents(fieldName);

      if (!targets.length) return;

      const ctx =
        this.normalizeContext(
          form.getRawValue()
        );

      targets.forEach((target: string) => {

        const rule = this.graph.get(target);
        if (!rule) return;

        const value = rule.fn(ctx);

        const ctrl = form.get(target);
        if (!ctrl) return;

        ctrl.setValue(value, {
          emitEvent: false
        });

        // 🔥 CASCADE EXECUTION
        run(target);
      });
    };

    run(changed);
  }

  private setupLineComputed(
    form: FormGroup,
    layout: DynamicFormLayout
  ) {

    const lineFields = Object.values(layout.fieldsMap)
      .filter(f => !!f.parentField && !!f.computedExpression);

    if (!lineFields.length) return;

    lineFields.forEach((field: DynamicFieldConfig) => {

      const arr = form.get(field.parentField!) as FormArray;
      if (!arr) return;

      const expr = this.parser.parse(field.computedExpression!);

      const attachRow = (row: FormGroup) => {

        // detect dependencies automatically
        const deps = expr.variables();

        deps.forEach((dep: string) => {

          row.get(dep)
            ?.valueChanges
            .subscribe(() => {

              try {

                const ctx =
                  this.normalizeContext(
                    row.getRawValue()
                  );

                row.get(field.name)
                  ?.setValue(
                    expr.evaluate(ctx),
                    {
                      emitEvent: false
                    }
                  );

              } catch (err) {

                console.error(
                  'LINE COMPUTE ERROR',
                  field.name,
                  err
                );
              }

            });

        });

        // ⭐ INITIAL compute
        try {
          const ctx =
            this.normalizeContext(
              row.getRawValue()
            );

          row.get(field.name)?.setValue(
            expr.evaluate(ctx),
            { emitEvent: false }
          );
        } catch {}

      };

      // existing rows
      arr.controls.forEach(r =>
        attachRow(r as FormGroup)
      );

    });
  }

  private setupRepeaterTriggers(
    form: FormGroup,
    layout: DynamicFormLayout
  ): void {

    const repeaters =
      this.getRepeaterConfigs(layout);

    repeaters.forEach(r => {

      const expr =
        r.lineConfig?.rowCountExpression;

      if (!expr) {
        return;
      }

      const matches =
        expr.match(
          /header\.([a-zA-Z0-9_]+)/g
        ) || [];

      matches.forEach(match => {

        const field =
          match.replace(
            'header.',
            ''
          );

        const key =
          `${r.name}_${field}`;

        if (
          this.repeaterTriggerSubscriptions.has(key)
        ) {
          return;
        }

        this.repeaterTriggerSubscriptions.add(key);

        console.log(
          'REPEATER SUBSCRIBE',
          key
        );

        form.get(field)
          ?.valueChanges
          .subscribe(() => {

            const arr =
              form.get(
                r.name
              ) as FormArray;

            // already has rows
            if (
              arr &&
              arr.length > 0
            ) {
              return;
            }

            this.syncRepeaterRows(
              form,
              layout,
              r.name
            );

          });

      });

    });

  }

    // createRepeaterRow(layout: DynamicFormLayout, parentName: string, initial?: any): FormGroup {
    //   const controls: Record<string, AbstractControl> = {};
    //   const children = Object.values(layout.fieldsMap).filter(f => f.parentField === parentName);
    //   children.forEach(c => {
    //     controls[c.name] = new FormControl(initial?.[c.name] ?? null, this.buildValidators(c));
    //   });
    //   return this.fb.group(controls);
    // }

   createRepeaterRow(
      layout: DynamicFormLayout,
      parentName: string,
      initial?: any
    ): FormGroup {

      const controls: Record<string, AbstractControl> = {};

      // IMPORTANT:
      // Get repeater definition first
      const repeater =
        layout.fieldsMap[parentName];

      if (!repeater) {
        return this.fb.group({});
      }

      // Use repeater.fields directly
      // Preserves JSON order
     const children =
        Object.values(layout.fieldsMap)
          .filter(
            (f: DynamicFieldConfig) =>
              f.parentField === parentName
          );

      children.forEach((c: DynamicFieldConfig) => {

        let value =
          initial?.[c.name] ?? null;

        // ------------------------------------
        // AUTOCOMPLETE
        // ------------------------------------

        if (
          c.type === 'autocomplete' &&
          value
        ) {

          if (
            typeof value === 'string' &&
            value.startsWith('{')
          ) {
            try {
              value =
                JSON.parse(value);
            }
            catch {
            }
          }

          if (
            typeof value === 'string' &&
            c.displaySource &&
            initial
          ) {

            const label =
              initial[
                c.displaySource
              ];

            if (label) {

              value = {
                id: value,
                label
              };
            }
          }

          if (
            typeof value === 'object' &&
            value !== null
          ) {

            value = {
              id:
                value.id ??
                value.value ??
                null,

              label:
                value.label ??
                value.name ??
                ''
            };
          }
        }

        controls[c.name] =
          new FormControl(
            value,
            this.buildValidators(c)
          );

      });

      const group =
        this.fb.group(controls);

      (group as any).__uid =
        this.nextUid();

      return group;
    }

    addRepeaterRow(
      form: FormGroup,
      layout: DynamicFormLayout,
      parentName: string,
      initial?: any
    ): FormGroup | null {

      const arr =
        form.get(parentName);

      if (!(arr instanceof FormArray))
        return null;

      const row =
        this.createRepeaterRow(
          layout,
          parentName,
          initial
        );

      if (
        arr.controls.includes(row)
      ) {
        return row;
      }

      arr.push(row);
      //
      console.log(
        'ADD REPEATER ROW',
        parentName
      );
      //
      this.attachComputedForRow(
        row,
        layout,
        parentName
      );
            console.log(
        'ATTACH TAX CHECK',
        parentName
      );

      if (parentName === 'lines') {

      console.log(
        'CALLING ATTACH TAX RUNTIME'
      );

      const cmp = (window as any).__lineItemTable;

      if (cmp) {
        cmp.attachTaxRuntime(row);
      }
    }

      return row;
    }

    private attachComputedForRow(
      row: FormGroup,
      layout: DynamicFormLayout,
      parentName: string
    ) {

      const fields =
        Object.values(layout.fieldsMap)
          .filter(
            f =>
              f.parentField ===
              parentName &&
              !!f.computedExpression
          );

      console.log(
        'COMPUTED FIELDS',
        fields.length
      );

      fields.forEach(field => {

        const expr =
          this.getCompiledExpression(
            field.computedExpression!
          );

        const deps =
          expr.variables();

        console.log(
          'ATTACH COMPUTED',
          field.name,
          deps
        );

        deps.forEach((dep: string) => {

          row.get(dep)
            ?.valueChanges
            .subscribe(() => {

              try {

                const ctx =
                  this.normalizeContext(
                    row.getRawValue()
                  );

                row.get(field.name)
                  ?.setValue(
                    expr.evaluate(ctx),
                    {
                      emitEvent:false
                    }
                  );

              } catch {}
            });
        });
      });
    }

    isVisible(name: string): boolean {
      const v = this.visibilityMap[name];
      return v !== false;
    }

    visibilityChanges() {
      return this.visibility$.asObservable();
    }

    getVisibilityMap() {
      return this.visibilityMap;
    }

    private applyVisibility(form: FormGroup, layout: DynamicFormLayout) {
      Object.values(layout.fieldsMap).forEach(f => {
        this.visibilityMap[f.name] = true;
      });
      this.visibility$.next({ ...this.visibilityMap });

      const withConditions =
            Object.values(layout.fieldsMap)
              .filter(f =>
                !!f.visibleIf ||
                !!(f as any).visibility?.when
              );
      if (!withConditions.length) return;
      
      const recompute = (raw: any) => {
        const ctx = { header: this.normalizeContext(raw) };

        for (const f of withConditions) {
          try {
            const exprText =
              f.visibleIf ||
              (f as any).visibility?.when;

           const visible =
            !!this
                .getCompiledExpression(
                  exprText
                )
                .evaluate(ctx);

            this.visibilityMap[f.name] = visible;

            // const ctrl = form.get(f.name);
            // if (ctrl) {
            //   if (!visible) ctrl.disable({ emitEvent: false });
            //   else ctrl.enable({ emitEvent: false });
            // }

            // HEADER FIELD
              if (!f.parentField) {
                const ctrl = form.get(f.name);
                if (ctrl) {
                  if (!visible) ctrl.disable({ emitEvent: false });
                  else ctrl.enable({ emitEvent: false });
                }
              }

              // LINE FIELD
              if (f.parentField) {
                const arr = form.get(f.parentField) as FormArray;
                if (arr) {
                  arr.controls.forEach(row => {
                    const ctrl = row.get(f.name);
                    if (!ctrl) return;

                    if (!visible) ctrl.disable({ emitEvent: false });
                    else ctrl.enable({ emitEvent: false });
                  });
                }
              }

            } catch {
              this.visibilityMap[f.name] = true;
            }
        }

        this.visibility$.next({ ...this.visibilityMap });
      };

      recompute(form.getRawValue());
      this.subscribeVisibilityDependencies(
        form,
        layout,
        recompute
      );
    }
    private subscribeVisibilityDependencies(
      form: FormGroup,
      layout: DynamicFormLayout,
      recompute: (
        raw: any
      ) => void
    ) {

      const watched =
        new Set<string>();

      Object.values(layout.fieldsMap)
        .forEach(f => {

          const expr =
            f.visibleIf ||
            (f as any)
              ?.visibility
              ?.when;

          if (!expr)
            return;

          const vars =
            this
              .getCompiledExpression(expr)
              .variables();

          vars.forEach((v: string) => {

            const field =
              v.replace(
                'header.',
                ''
              );

            if (
              watched.has(field)
            ) return;

            watched.add(field);

            form.get(field)
              ?.valueChanges
              .subscribe(() => {

                recompute(
                  form.getRawValue()
                );

              });

          });

          console.log(
            'visibility vars',
            vars
          );

        });
    }

    // private normalizeContext(raw: any): any {
    //   const ctx: any = {};
    //   Object.keys(raw || {}).forEach(k => {
    //     const v = raw[k];
    //     if (v && typeof v === 'object') {
    //       ctx[k] = v.value ?? v.id ?? v;
    //     } else {
    //       ctx[k] = v;
    //     }
    //   });
    //   return ctx;
    // }
    private normalizeContext(raw: any): any {
      const ctx: any = {};
      Object.keys(raw || {}).forEach(k => {
        ctx[k] = this.normalizeValue(raw[k]);
      });
      return ctx;
    }
    
    private normalizeValue(v: any): any {
      if (v === null || v === undefined) return v;

      // autocomplete object
      if (typeof v === 'object') {

        if ('id' in v && v.id !== undefined)
          return v.id;

        if ('value' in v && v.value !== undefined)
          return v.value;

        if ('code' in v && v.code !== undefined)
          return v.code;

        if ('label' in v && Object.keys(v).length === 2)
          return v.label;
      }

      return v;
    }

    private applyRequired(form: FormGroup, layout: DynamicFormLayout) {
      const fields = Object.values(layout.fieldsMap).filter(f => !!f.requiredWhen);
      if (!fields.length) return;

      const apply = () => {
        const raw = form.getRawValue();

        fields.forEach(f => {
          const ctrl = form.get(f.name);
          if (!ctrl) return;

          const shouldBeRequired = !!this.evalExpr(f.requiredWhen!, raw);

          const validators = ctrl.validator ? [ctrl.validator] : [];
          ctrl.clearValidators();

          if (shouldBeRequired) validators.push(Validators.required);

          ctrl.setValidators(validators);
          ctrl.updateValueAndValidity({ emitEvent: false });
        });
      };

      apply();
      form.valueChanges.subscribe(() => {
        apply();
      });
    }

    private applySoftMax(form: FormGroup, layout: DynamicFormLayout) {

      const fields = Object.values(layout.fieldsMap)
        .filter(f => !!f.softMax);

      if (!fields.length) return;

      const apply = () => {

        // HEADER CONTEXT (normalized)
        const headerCtx = this.normalizeContext(form.getRawValue());

        fields.forEach(f => {

          const rule = f.softMax!;

          // ALWAYS evaluate using { header }
          const active =
            !rule.when ||
            this.evalExpr(rule.when, { header: headerCtx });

          if (!active) {

            // HEADER FIELD
            if (!f.parentField) {
              const ctrl = form.get(f.name);
              if (ctrl) {
                ctrl.setValidators(this.buildValidators(f));
                ctrl.updateValueAndValidity({ emitEvent: false });
              }
            }

            // LINE FIELD
            if (f.parentField) {
              const arr = form.get(f.parentField) as FormArray;

              arr?.controls.forEach(row => {
                const ctrl = row.get(f.name);
                if (!ctrl) return;

                ctrl.setValidators(this.buildValidators(f));
                ctrl.updateValueAndValidity({ emitEvent: false });
              });
            }

            return;
          }

          // ---------------- HEADER FIELD ----------------
          if (!f.parentField) {

            const ctrl = form.get(f.name);
            if (!ctrl) return;

            const maxVal = this.toNumber(
              this.evalExpr(rule.expr, { header: headerCtx })
            );

            const v: ValidatorFn = c =>
              this.toNumber(c.value) > maxVal ? { softMax: true } : null;

            ctrl.setValidators([
              ...this.buildValidators(f),
              v
            ]);

            ctrl.updateValueAndValidity({ emitEvent: false });
          }

          // ---------------- LINE FIELD ----------------
        if (f.parentField) {

            const arr = form.get(f.parentField) as FormArray;
            if (!arr) return;

            arr.controls.forEach(row => {

              const ctrl = row.get(f.name);
              if (!ctrl) return;

              const rowCtx = {
                header: headerCtx,
                line: this.normalizeContext(row.getRawValue())
              };

              const rowMax = this.toNumber(
                this.evalExpr(rule.expr, rowCtx)
              );

              const v: ValidatorFn = c =>
              this.toNumber(c.value) > rowMax
                ? {
                    softMax: {
                      field: f.label || f.name,
                      max: rowMax
                    }
                  }
                : null;

              ctrl.setValidators([
                ...this.buildValidators(f),
                v
              ]);

              ctrl.updateValueAndValidity({ emitEvent: false });
            });
          }


        });
      };

      // Initial apply
      apply();

      //
      fields.forEach((f: DynamicFieldConfig) => {

        const exprs: string[] = [];

        exprs.push(
          f.softMax!.expr
        );

        if (f.softMax!.when) {

          exprs.push(
            f.softMax!.when!
          );
        }

        exprs.forEach(expr => {

          this.subscribeExpressionDependencies(
            form,
            expr,
            apply
          );

        });

      });
    }

    private toNumber(v: any): number {
      if (v === null || v === undefined || v === '') return 0;

      const n = Number(v);
      return isNaN(n) ? 0 : n;
    }


    public reapplySoftMax(form: FormGroup, layout: DynamicFormLayout) {
      this.applySoftMax(form, layout);
    }

    private executeDataFlow(
      form: FormGroup,
      layout: DynamicFormLayout,
      changed?: string
    ) {

      if (!changed) return;

      const flows = (layout as any).dataFlow || [];

      flows.forEach((flow: any) => {

        const source =
          (flow.when || '')
            .replace('header.', '');

        if (source !== changed) return;

        if (this.suppressDataFlow) return;

        flow.clear?.forEach((field : string)=> {

        const ctrl =
          form.get(field);

        if (!ctrl) {
          return;
        }

        // HEADER FIELD
        if (
          ctrl instanceof FormControl
        ) {

          ctrl.setValue(
            null,
            { emitEvent: false }
          );

          return;
        }

        // REPEATER
        if (ctrl instanceof FormArray) {

          ctrl.clear({
            emitEvent:false
          });

          return;
        }

      });

      });
    }

    runWithoutDataFlow(fn: () => void) {
      this.suppressDataFlow = true;
      try {
        fn();
      } finally {
        this.suppressDataFlow = false;
      }
    }

    private applyStateRules(form: FormGroup, layout: DynamicFormLayout) {

      const fields = Object.values(layout.fieldsMap)
        .filter(f => !!f.state?.disabledWhen);

      console.log('call applyStateRules fields : ',fields)
      if (!fields.length) return;

      const apply = () => {

        console.log(
          'STATE APPLY CALLED'
        );

        const headerCtx =
          this.normalizeContext(form.getRawValue());
        
        fields.forEach(f => {

          const shouldDisable = !!this.evalExpr(
            f.state?.disabledWhen || '',
            { header: headerCtx }
          );

          console.log(
            'STATE RULE',
            f.name,
            f.state?.disabledWhen,
            'RESULT:',
            shouldDisable,
            'VALUE:',
            headerCtx.customer_id
          );


          // HEADER FIELD
          if (!f.parentField) {
            const ctrl = form.get(f.name);
            if (!ctrl) return;

            shouldDisable
              ? ctrl.disable({ emitEvent: false })
              : ctrl.enable({ emitEvent: false });
          }

          // LINE FIELD
          if (f.parentField) {

            const arr = form.get(f.parentField) as FormArray;
            if (!arr) return;

            arr.controls.forEach(row => {

              const ctrl = row.get(f.name);
              if (!ctrl) return;

              shouldDisable
                ? ctrl.disable({ emitEvent: false })
                : ctrl.enable({ emitEvent: false });
            });
          }
        });
      };

      apply();
      //
      fields.forEach((f: DynamicFieldConfig) => {

        const key =
          `STATE_${f.name}`;

        if (
          this.expressionSubscriptions.has(key)
        ) {
          return;
        }

        this.expressionSubscriptions.add(key);

        this.subscribeExpressionDependencies(
          form,
          f.state?.disabledWhen || '',
          apply
        );

      });
    }


    // private buildValidators(config: DynamicFieldConfig): ValidatorFn[] {
    //   const v: ValidatorFn[] = [];
    //   if (config.required) v.push(Validators.required);
    //   const rules = config.validators;
    //   if (!rules) return v;
    //   if (rules.min !== undefined) v.push(Validators.min(rules.min));
    //   if (rules.max !== undefined) v.push(Validators.max(rules.max));
    //   if (rules.pattern) v.push(Validators.pattern(rules.pattern));
    //   return v;
    // }

    private buildValidators(
      config: DynamicFieldConfig
    ): ValidatorFn[] {

      const validators: ValidatorFn[] = [];

      // ===========================
      // Standard Validators
      // ===========================

      if (config.required) {
        validators.push(
          Validators.required
        );
      }

      const rules =
        config.validators;

      if (rules) {

        if (rules.min !== undefined) {
          validators.push(
            Validators.min(rules.min)
          );
        }

        if (rules.max !== undefined) {
          validators.push(
            Validators.max(rules.max)
          );
        }

        if (rules.pattern) {
          validators.push(
            Validators.pattern(
              rules.pattern
            )
          );
        }
      }

      // ===========================
      // Date Validators
      // ===========================

      const dateCfg =
        config.dateConfig;

      if (dateCfg) {

        if (
          dateCfg.allowPastDate === false
        ) {

          validators.push(
            DateValidators.noPastDate()
          );
        }

        if (
          dateCfg.allowFutureDate === false
        ) {

          validators.push(
            DateValidators.noFutureDate()
          );
        }

        // Financial Year validator
        // Will be attached later once FY
        // is loaded from backend.
      }

      return validators;
    }

    // BUILD SUBMIT PAYLOAD (Option A - lines inside DataDynamic)
    getSubmitPayload(form: FormGroup, layout: DynamicFormLayout): any {
      const staticObj: any = {};
      Object.values(layout.fieldsMap)
        .filter(f => !f.parentField && f.category === 'static')
        .forEach(f => {
          const v = form.get(f.name)?.value;
          if (v !== undefined) staticObj[f.name] = v;
        });

      const dynamicObj: any = {};
      Object.values(layout.fieldsMap)
        .filter(f => !f.parentField && f.category === 'dynamic')
        .forEach(f => {
          const v = form.get(f.name)?.value;
          if (v !== undefined) dynamicObj[f.name] = v;
        });

      const parents = new Set<string>();
      Object.values(layout.fieldsMap).forEach(f => { if (f.parentField) parents.add(f.parentField!); });

      parents.forEach(parentName => {
        const arr = form.get(parentName) as FormArray;
        if (!arr) { dynamicObj[parentName] = []; return; }
        const rows: any[] = [];
        for (const rowCtrl of arr.controls) {
          const rowObj: any = {};
          Object.values(layout.fieldsMap).filter(ch => ch.parentField === parentName).forEach(ch => {
            const val = rowCtrl.get(ch.name)?.value;
            if (val !== undefined) rowObj[ch.name] = val;
          });
          rows.push(rowObj);
        }
        dynamicObj[parentName] = rows;
      });

      return { DataStatic: staticObj, DataDynamic: dynamicObj };
    }

    // Event API (unchanged)
    fireEvent(fieldName: string, label: string, payload: any) {
      const key = `${fieldName}.${label}`;
      this.eventBus[key]?.next(payload);
    }

    onEvent(fieldName: string, label: string) {
      const key = `${fieldName}.${label}`;
      if (!this.eventBus[key]) this.eventBus[key] = new Subject<any>();
      return this.eventBus[key].asObservable();
    }

    private evalExpr(
      expr: string,
      ctx: any
    ): any {

      try {

        return this
          .getCompiledExpression(expr)
          .evaluate(ctx);

      } catch {

        return false;
      }
    }
    private getCompiledExpression(expr: string) {

      let compiled =
        this.expressionCache.get(expr);

      if (!compiled) {

        compiled =
          this.parser.parse(expr);

        this.expressionCache.set(
          expr,
          compiled
        );
      }

      return compiled;
    }

    private getRepeaterConfigs(
      layout: DynamicFormLayout
    ): DynamicFieldConfig[] {

      return Object.values(layout.fieldsMap)
        .filter(f => {

          const hasChildren =
            Object.values(layout.fieldsMap)
              .some(x =>
                x.parentField === f.name
              );

          return hasChildren;
        });
    }

    public initializeRepeaterDefaults(
      form: FormGroup,
      layout: DynamicFormLayout
    ): void {

      const repeaters =
        this.getRepeaterConfigs(
          layout
        );

      repeaters.forEach(r => {

        if (
          !r.lineConfig?.autoCreateRows
        )
          return;

        this.syncRepeaterRows(
          form,
          layout,
          r.name
        );

      });
    }

    private getDesiredRowCount(
      repeater: DynamicFieldConfig,
      form: FormGroup
    ): number {

      const cfg =
        repeater.lineConfig;

      if (!cfg)
        return 0;

      try {

        if (cfg.rowCountExpression) {

          const ctx = {
            header: this.normalizeContext(
              form.getRawValue()
            )
          };

          const result =
            Number(
              this
                .getCompiledExpression(
                  cfg.rowCountExpression
                )
                .evaluate(ctx)
            );

          if (result <= 0) {

            return result === 0
              ? 0
              : cfg.initialRows ?? 0;
          }

          return result;
        }

        return cfg.initialRows ?? 0;
      }
      catch {

        return cfg.initialRows ?? 0;
      }
    }

    private syncingRepeaters =new Set<string>();
    public syncRepeaterRows(
      form: FormGroup,
      layout: DynamicFormLayout,
      repeaterName: string
    ): void {
      if (
        this.syncingRepeaters.has(
          repeaterName
        )
      ) {
        return;
      }

      this.syncingRepeaters.add(
        repeaterName
      );

      try {

      const cfg =
        layout.fieldsMap[
          repeaterName
        ];

      if (!cfg)
        return;

      const arr =
        form.get(
          repeaterName
        ) as FormArray;

      if (!arr)
        return;

      let desired =
        this.getDesiredRowCount(
          cfg,
          form
        );

      if (
        desired > 0
      )
      {
        desired =
          Math.max(
            cfg.lineConfig?.minRows ?? 0,
            desired
          );
      }

      console.log(
        'SYNC REPEATER',
        repeaterName,
        'current',
        arr.length,
        'desired',
        desired
      );

      if (
        arr.length > 0 &&
        desired > 0
      ) {

        console.log(
          'SKIP REBUILD',
          repeaterName
        );

        return;
      }

      if (
        arr.length === desired
      ) {
        return;
      }

      if (
          arr.length < desired
        ) {

          const missing =
            desired - arr.length;

          for (
            let i = 0;
            i < missing;
            i++
          ) {

            this.addRepeaterRow(
              form,
              layout,
              repeaterName
            );
          }

        }

      while (
        arr.length > desired
      ) {

        arr.removeAt(
          arr.length - 1
        );
      }
      }
      finally {

        this.syncingRepeaters.delete(
          repeaterName
        );

      }
    }

    private nextUid(): string {

      this.uidCounter++;

      return `row_${this.uidCounter}`;
    }

    private dependencySubscriptions = new Set<string>();
    private subscribeExpressionDependencies(
      form: FormGroup,
      expression: string,
      callback: () => void
    ): void {

      if (!expression) {
        return;
      }

      try {

        const matches =
          expression.match(
            /header\.([a-zA-Z0-9_]+)/g
          ) || [];

        matches.forEach(match => {

          const field =
            match.replace(
              'header.',
              ''
            );

          const key =
            `${field}_${expression}`;

          if (
            this.dependencySubscriptions.has(key)
          ) {
            return;
          }

          this.dependencySubscriptions.add(key);

          console.log(
            'SUBSCRIBE',
            key
          );

          form.get(field)
            ?.valueChanges
            .subscribe(() => {

              console.log(
                'DEPENDENCY FIRED',
                field
              );

              callback();

            });

        });

      }
      catch (err) {

        console.error(
          'DEPENDENCY ERROR',
          err
        );
      }
    }

    public getTaxConfig(
      layout: any
    ): EntityTaxConfig {

      return (
        layout?.taxConfig ??
        {
          header: {

            organizationField: 'org_id',
            customerField: 'customer_id',
            currencyField: 'currency_id'

          },

          line: {

            itemField: 'item_id',
            quantityField: 'quantity',
            priceField: 'unit_price',

            taxGroupIdField: 'tax_group_id',
            taxGroupNameField: 'tax_group_name',

            taxAmountField: 'tax_amount',
            baseAmountField: 'line_total',
            grossAmountField: 'line_grand_total',

            taxLinesField: 'tax_lines'
          },

          summary: {

            subtotalField: 'subtotal',
            taxTotalField: 'tax_total',
            grandTotalField: 'grand_total'
          }
        }
      );

    }

    private applyDateDefaults(
      field: DynamicFieldConfig,
      control: FormControl
    ): void {

      const cfg =
        field.dateConfig;

      if (!cfg) {
        return;
      }

      if (
        cfg.defaultValue === 'today' &&
        !control.value
      ) {

        control.patchValue(
          DateUtils.today(),
          {
            emitEvent:false
          }
        );
      }
    }

    private buildDateValidators(
      field: DynamicFieldConfig
    ): ValidatorFn[] {

      const validators: ValidatorFn[] = [];

      const cfg =
        field.dateConfig;

      if (!cfg) {
        return validators;
      }

      if (
        cfg.allowPastDate === false
      ) {

        validators.push(
          DateValidators.noPastDate()
        );
      }

      if (
        cfg.allowFutureDate === false
      ) {

        validators.push(
          DateValidators.noFutureDate()
        );
      }

      return validators;
    }

  }


